import React, { useState, useEffect } from "react";
import '../App.css';
import { useLocation } from 'react-router-dom';
import './recipeengine.css';
import './recipeanalysis.css';
import StoreLocator from './storelocator';
import ChatInterface from './chatinterface';
import { useSubscription } from '../subscription/subscriptioncontext';

function Planner() {
    const location = useLocation();
    const { trackUsage, subscription, loading: subscriptionLoading } = useSubscription();
    const [loading, setLoading] = useState(false);
    const [, setError] = useState(null);
    const [selectedVideos, setSelectedVideos] = useState([]);
    const [videos, setVideos] = useState([]);
    const [analysisResults, setAnalysisResults] = useState(null);

    const [showAnalysis, setShowAnalysis] = useState(false);
    const [showUrlForm, setShowUrlForm] = useState(false);
    const [urlInput, setUrlInput] = useState('');
    const [activeTab, setActiveTab] = useState('externally'); // New state for tab management
    const [storesList, setStoresList] = useState([]);
    // Chat interface state for "Internally" tab
    const [showChatInterface, setShowChatInterface] = useState(false);
    const [recipeStores, setRecipeStores] = useState({}); // Store stores per recipe index
    const [saveButtonHovered, setSaveButtonHovered] = useState({});
    const [savedRecipeStatus, setSavedRecipeStatus] = useState({}); // Track which recipes are saved
    const [permanentlySavedRecipes, setPermanentlySavedRecipes] = useState({}); // Track permanently saved recipes
    const [editingRecipeName, setEditingRecipeName] = useState({}); // Track which recipe names are being edited
    const [tempRecipeNames, setTempRecipeNames] = useState({}); // Store temporary recipe names while editing
    
    // Get API base URL from environment variable
    const apiBaseUrl = process.env.REACT_APP_RAILWAY_URL || 'http://localhost:8000';

    useEffect(() => {
        // Clear everything when component mounts to fix user session bug
        setVideos([]);
        setSelectedVideos([]);
        setAnalysisResults(null);
        setError(null);
        
        // Clear TikTok token to prevent cross-user contamination
        const params = new URLSearchParams(location.search);
        const hasNewToken = params.get('access_token');
        
        // Only clear token if we don't have a new one coming in
        if (!hasNewToken) {
            localStorage.removeItem('tiktok_access_token');
        }
        
        const fetchTikTokVideos = async () => {
            // Wait for subscription to load before proceeding
            if (subscriptionLoading) {
                return;
            }
            
            // Check for access token in URL parameters
            let token = params.get('access_token');
            
            // If no token in URL, check localStorage
            if (!token) {
                token = localStorage.getItem('tiktok_access_token');
            } else {
                // Save token to localStorage for future use
                localStorage.setItem('tiktok_access_token', token);
                
                // Clean up URL if we're on the standalone planner page
                if (location.pathname === '/planner') {
                    window.history.replaceState({}, document.title, '/planner');
                }
            }
            
            if (!token) {
                return;
            }

            setLoading(true);
            try {
                const response = await fetch(`${apiBaseUrl}/api/tiktok/videos/`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch videos: ${response.status}`);
                }

                const data = await response.json();
                setVideos(data.videos || []);
            } catch (err) {
                setError('Failed to load TikTok videos');
                // Clear token on error to prevent repeated failures
                localStorage.removeItem('tiktok_access_token');
            } finally {
                setLoading(false);
            }
        };

        fetchTikTokVideos();
    }, [location.search, location.pathname, apiBaseUrl, subscriptionLoading]);

    const handleTikTokLogin = async () => {
        try {
            // Clear any existing videos and tokens before new login
            setVideos([]);
            setSelectedVideos([]);
            localStorage.removeItem('tiktok_access_token');
            
            const scopes = [
                'user.info.basic',
                'user.info.profile',
                'user.info.stats',
                'video.list'
            ].join(',');
            
            const state = Math.random().toString(36).substring(7);
            
            const authUrl = `https://www.tiktok.com/v2/auth/authorize?` +
                `client_key=${process.env.REACT_APP_TIKTOK_CLIENT_KEY}` +
                `&scope=${scopes}` +
                `&response_type=code` +
                `&redirect_uri=${process.env.REACT_APP_TIKTOK_REDIRECT_URI}` +
                `&state=${state}`;
            
            window.location.href = authUrl;
        } catch (error) {
            setError('Failed to connect to TikTok');
        }
    };

    const handlePasteUrl = () => {
        setShowUrlForm(true);
    };

    const toggleVideoSelection = (video) => {
        setSelectedVideos(prev => {
            const isSelected = prev.some(v => v.id === video.id);
            if (isSelected) {
                return prev.filter(v => v.id !== video.id);
            } else {
                return [...prev, video];
            }
        });
    };

    const analyzeSelectedVideos = async () => {
        console.log('🔍 Starting analysis for videos:', selectedVideos);
        setLoading(true);
        setError(null); // Clear any previous errors
        
        try {
            const descriptions = selectedVideos.map(video => ({
                id: video.id,
                title: video.title,
                description: video.video_description
            }));

            console.log('📤 Sending to backend:', descriptions);

            const response = await fetch(`${apiBaseUrl}/api/claude/analyze-recipes/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ videos: descriptions })
            });

            console.log('📡 Backend response status:', response.status);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('❌ Backend error:', errorText);
                throw new Error(`Failed to analyze videos: ${response.status} - ${errorText}`);
            }

            const analysis = await response.json();
            console.log('✅ Analysis received:', analysis);
            
            setAnalysisResults(analysis);
            setShowAnalysis(true);
        } catch (error) {
            console.error('❌ Analysis error:', error);
            setError(`Failed to analyze videos: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateRecipe = () => {
        setShowChatInterface(true);
    };

    // Handle when recipes are confirmed in chat and transition to analysis
    const handleRecipesConfirmed = (recipesData) => {
        console.log('Recipes confirmed:', recipesData);
        
        // Set the analysis results with the confirmed recipes
        setAnalysisResults({
            ...recipesData,
            source_url: '', // No source URL for chat-generated recipes
            analysis: 'Generated through guided conversation with Penny'
        });
        
        // Hide chat interface and show analysis
        setShowChatInterface(false);
        setShowAnalysis(true);
        
        // Clear any previous video selections since these are chat-generated
        setSelectedVideos([]);
        setVideos([]);
    };

    // Add this function to receive stores from StoreLocator
    const handleStoresUpdate = (stores, recipeIndex) => {
        console.log(`Updating stores for recipe ${recipeIndex}:`, stores);
        setRecipeStores(prev => ({
            ...prev,
            [recipeIndex]: stores
        }));
        // Also keep the legacy storesList for backward compatibility
        setStoresList(stores);
    };

    // Functions for editable recipe names
    const handleRecipeNameClick = (index) => {
        const currentName = analysisResults.recipes[index].name || "Untitled Recipe";
        setEditingRecipeName(prev => ({...prev, [index]: true}));
        setTempRecipeNames(prev => ({...prev, [index]: currentName}));
    };

    const handleRecipeNameChange = (index, newName) => {
        setTempRecipeNames(prev => ({...prev, [index]: newName}));
    };

    const handleRecipeNameSave = (index) => {
        const newName = tempRecipeNames[index] || "Untitled Recipe";
        // Update the analysisResults with the new name
        setAnalysisResults(prev => ({
            ...prev,
            recipes: prev.recipes.map((recipe, i) => 
                i === index ? {...recipe, name: newName} : recipe
            )
        }));
        setEditingRecipeName(prev => ({...prev, [index]: false}));
        setTempRecipeNames(prev => ({...prev, [index]: undefined}));
    };

    const handleRecipeNameCancel = (index) => {
        setEditingRecipeName(prev => ({...prev, [index]: false}));
        setTempRecipeNames(prev => ({...prev, [index]: undefined}));
    };

    // Handle click outside to cancel editing
    const handleRecipeNameBlur = (index) => {
        // Small delay to allow save action if user clicked save button
        setTimeout(() => {
            if (editingRecipeName[index]) {
                handleRecipeNameCancel(index);
            }
        }, 100);
    };

    // Handle saving a recipe
    const handleSaveRecipe = async (recipe, recipeIndex) => {
        try {
            // Get user data from localStorage
            const userData = JSON.parse(localStorage.getItem('user') || '{}');
            if (!userData.id) {
                alert('Please log in to save recipes');
                return;
            }

            // Prepare recipe data for saving
            // Determine source based on how recipe was created
            let recipeSource = 'Manual Entry';
            if (selectedVideos && selectedVideos.length > 0) {
                recipeSource = 'TikTok';
            } else if (showUrlForm || analysisResults?.source_url) {
                recipeSource = 'URL';
            }

            const recipeData = {
                user_id: userData.id,
                name: recipe.name || 'Untitled Recipe',
                ingredients: recipe.ingredients || [],
                instructions: recipe.steps || [],
                cooking_time: recipe.cookingTime,
                servings: recipe.servings,
                source: recipeSource,
                source_url: analysisResults?.source_url || '',
                analysis_data: {
                    original_analysis: analysisResults?.analysis,
                    created_from: 'recipe_engine',
                    stores: recipeStores[recipeIndex] || storesList || [] // Use recipe-specific stores
                }
            };

            console.log('Saving recipe:', recipeData);
            console.log('Recipe stores:', recipeStores[recipeIndex]);
            console.log('Legacy stores list:', storesList);

            const response = await fetch(`${apiBaseUrl}/api/save-recipe/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(recipeData)
            });

            const data = await response.json();

            if (response.ok && data.success) {
                // Mark recipe as saved with green checkmark (temporary feedback)
                setSavedRecipeStatus(prev => ({...prev, [recipeIndex]: 'saved'}));
                console.log('Recipe saved:', data.recipe);
                
                // After 2 seconds, mark as permanently saved and disable button
                setTimeout(() => {
                    setSavedRecipeStatus(prev => ({...prev, [recipeIndex]: null}));
                    setPermanentlySavedRecipes(prev => ({...prev, [recipeIndex]: true}));
                }, 2000);
            } else {
                console.error(`Failed to save recipe: ${data.error || 'Unknown error'}`);
            }

        } catch (error) {
            console.error('Error saving recipe:', error);
            alert('Failed to save recipe. Please try again.');
        }
    };

    // Show loading while subscription is loading
    if (subscriptionLoading) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '50vh' 
            }}>
                <div>Loading...</div>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column'
        }}>
            <div style={{
                flex: 1,
                margin: '0 auto',
                width: '100%',
                padding: '0 20px'
            }}>
                    <div style={{
                        marginBottom: '40px'
                    }}>
                        {/* Tab Navigation */}
                        <div className="recipe-tabs" style={{
                            display: 'flex',
                            gap: '40px',
                            marginBottom: '30px',
                            paddingBottom: '0'
                        }}>
                            <div 
                                className={`tab-item ${activeTab === 'externally' ? 'active' : ''}`}
                                onClick={() => setActiveTab('externally')}
                                style={{
                                    fontSize: '1.1rem',
                                    fontWeight: '500',
                                    color: activeTab === 'externally' ? '#0B303B' : '#666',
                                    cursor: 'pointer',
                                    paddingBottom: '15px',

                                    position: 'relative'
                                }}

                            >
                                Externally
                            </div>
                            <div 
                                className={`tab-item ${activeTab === 'internally' ? 'active' : ''}`}
                                onClick={() => setActiveTab('internally')}
                                style={{
                                    fontSize: '1.1rem',
                                    fontWeight: '500',
                                    color: activeTab === 'internally' ? '#0B303B' : '#666',
                                    cursor: 'pointer',
                                    paddingBottom: '15px',

                                    position: 'relative'
                                }}

                            >
                                Internally
                            </div>
                        </div>
                        
                        <h1 style={{ 
                            marginBottom: '20px',
                            color: '#0B303B',
                            fontWeight: '500',
                            fontSize: '2.5rem',
                            textAlign: 'left',
                            margin: '0 0 2rem 0'
                        }}>
                            Recipe Engine
                        </h1>
                        {/* Commented out for now - might use elsewhere */}
                        {/*
                        <ul style={{
                            listStyle: 'none',
                            padding: 0,
                            margin: '0 0 40px 0'
                        }}>
                            <li style={{
                                margin: '10px 0',
                                display: 'flex',
                                alignItems: 'center',
                                color: '#666'
                            }}>
                                <span style={{ marginRight: '10px' }}>✓</span>
                                Get AI-powered recipe analysis
                            </li>
                            <li style={{
                                margin: '10px 0',
                                display: 'flex',
                                alignItems: 'center',
                                color: '#666'
                            }}>
                                <span style={{ marginRight: '10px' }}>✓</span>
                                Access optimized routes for your favorite recipes
                            </li>
                            <li style={{
                                margin: '10px 0',
                                display: 'flex',
                                alignItems: 'center',
                                color: '#666'
                            }}>
                                <span style={{ marginRight: '10px' }}>✓</span>
                                Minimize time and money spent on groceries
                            </li>
                        </ul>
                        <p style={{
                            fontSize: '1.1em',
                            lineHeight: '1.6',
                            color: '#666',
                            
                        }}>
                            {activeTab === 'externally' 
                                ? 'Welcome to your personal Recipe Assistant! Connect your TikTok account to review your favorite recipes or paste a link to any other recipe you would like to extract:'
                                : 'Welcome to your personal Recipe Assistant! Create your own custom recipes from scratch or use ingredients you have at home:'
                            }
                        </p>
                        */}
                    </div>
                    
                    {/* Analysis Results Section - Integrated into Recipe Engine */}
                    {showAnalysis && analysisResults ? (
                        <div style={{ marginBottom: '40px' }}>
                            {/* Back and Extract buttons styled like Recipe Engine */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: '30px',
                                width: '100%',
                                justifyContent: 'flex-start',
                                padding: '0'
                            }}>
                                <div 
                                    className="action-button"
                                    onClick={() => {
                                        setShowAnalysis(false);
                                        setAnalysisResults(null);
                                    }}
                                    style={{
                                        fontSize: '1.1rem',
                                        fontWeight: '500',
                                        color: '#666',
                                        cursor: 'pointer',
                                        paddingBottom: '5px',
                                        position: 'relative'
                                    }}
                                >
                                    Back
                                </div>
                            </div>

                            {/* Recipe cards with ingredients, instructions, and stores */}
                            <div className="recipes-grid">
                                {(analysisResults?.recipes || []).map((recipe, index) => (
                                    <div key={index} style={{ 
                                        display: 'flex', 
                                        alignItems: 'stretch', 
                                        gap: '10px',
                                        marginBottom: '20px',
                                        position: 'relative'
                                    }}>
                                                                                {/* Save Recipe Button Wrapper */}
                                        <div style={{
                                            width: '50px',
                                            alignSelf: 'stretch',
                                            flexShrink: 0,
                                            position: 'relative',
                                            marginTop: '20px',
                                            marginBottom: '60px'
                                        }}>
                                            <div
                                                style={{
                                                    width: (saveButtonHovered[index] || savedRecipeStatus[index] === 'saved' || permanentlySavedRecipes[index]) ? '160px' : '30px',
                                                    height: '100%',
                                                    position: 'absolute',
                                                    right: '0',
                                                    top: '0',
                                                    backgroundColor: 'transparent',
                                                    border: `2px solid ${permanentlySavedRecipes[index] ? '#28a745' : '#ccc'}`,
                                                    borderRadius: (saveButtonHovered[index] || savedRecipeStatus[index] === 'saved' || permanentlySavedRecipes[index]) ? '12px' : '8px',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: permanentlySavedRecipes[index] ? 'not-allowed' : 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                                    zIndex: (saveButtonHovered[index] || savedRecipeStatus[index] === 'saved' || permanentlySavedRecipes[index]) ? 10 : 1,
                                                    opacity: permanentlySavedRecipes[index] ? 0.8 : 1
                                                }}
                                                onMouseEnter={() => !permanentlySavedRecipes[index] && setSaveButtonHovered(prev => ({...prev, [index]: true}))}
                                                onMouseLeave={() => !permanentlySavedRecipes[index] && setSaveButtonHovered(prev => ({...prev, [index]: false}))}
                                                onClick={() => !permanentlySavedRecipes[index] && handleSaveRecipe(recipe, index)}
                                            >
                                                {permanentlySavedRecipes[index] ? (
                                                    // Permanently saved state
                                                    <>
                                                        <div style={{
                                                            color: '#28a745',
                                                            fontSize: '0.9rem',
                                                            fontWeight: '500',
                                                            marginBottom: '10px',
                                                            textAlign: 'center',
                                                            padding: '0 8px'
                                                        }}>
                                                            Recipe Saved
                                                        </div>
                                                        <div style={{
                                                            color: '#28a745',
                                                            fontSize: '2rem',
                                                            fontWeight: '300'
                                                        }}>
                                                            ✓
                                                        </div>
                                                    </>
                                                ) : saveButtonHovered[index] || savedRecipeStatus[index] === 'saved' ? (
                                                    // Hover or temporary saved state
                                                    <>
                                                        <div style={{
                                                            color: savedRecipeStatus[index] === 'saved' ? '#28a745' : '#0B303B',
                                                            fontSize: '0.9rem',
                                                            fontWeight: '500',
                                                            marginBottom: '10px',
                                                            textAlign: 'center',
                                                            padding: '0 8px'
                                                        }}>
                                                            {savedRecipeStatus[index] === 'saved' ? 'Saved' : 'Save Recipe'}
                                                        </div>
                                                        <div style={{
                                                            color: savedRecipeStatus[index] === 'saved' ? '#28a745' : '#0B303B',
                                                            fontSize: '2rem',
                                                            fontWeight: '300'
                                                        }}>
                                                            {savedRecipeStatus[index] === 'saved' ? '✓' : '+'}
                                                        </div>
                                                    </>
                                                ) : (
                                                    // Default collapsed state
                                                    <div style={{
                                                        color: '#0B303B',
                                                        fontSize: '1.5rem',
                                                        fontWeight: '300'
                                                    }}>
                                                        &lt;
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <div className="recipe-container-horizontal">
                                        {/* Ingredients Card */}
                                        <div className="recipe-card ingredients-card">
                                            <div style={{ marginBottom: '15px' }}>
                                                {editingRecipeName[index] ? (
                                                    <input
                                                        type="text"
                                                        value={tempRecipeNames[index] || ''}
                                                        onChange={(e) => handleRecipeNameChange(index, e.target.value)}
                                                        style={{
                                                            fontSize: '1.4rem',
                                                            fontWeight: '600',
                                                            color: '#333',
                                                            border: '1px solid #ccc',
                                                            borderRadius: '4px',
                                                            padding: '5px 10px',
                                                            background: 'white',
                                                            width: '100%',
                                                            outline: 'none'
                                                        }}
                                                        autoFocus
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') handleRecipeNameSave(index);
                                                            if (e.key === 'Escape') handleRecipeNameCancel(index);
                                                        }}
                                                        onBlur={() => handleRecipeNameBlur(index)}
                                                    />
                                                ) : (
                                                    <h3 
                                                        style={{
                                                            fontSize: '1.4rem',
                                                            fontWeight: '600',
                                                            color: '#333',
                                                            margin: 0,
                                                            cursor: 'pointer',
                                                            padding: '5px',
                                                            borderRadius: '4px',
                                                            transition: 'background-color 0.2s ease'
                                                        }}
                                                        onClick={() => handleRecipeNameClick(index)}
                                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                        title="Click to edit recipe name"
                                                    >
                                                        {recipe.name || "Untitled Recipe"}
                                                    </h3>
                                                )}
                                            </div>
                                            <div className="recipe-meta">
                                                <span>🕒 {recipe.cookingTime || 'Time not specified'}</span>
                                                <span>👥 {recipe.servings || 'Servings not specified'}</span>
                                            </div>
                                            <h3 style={{
                                                fontSize: '1.4rem',
                                                fontWeight: '600',
                                                color: '#333',
                                                margin: '20px 0 15px 0'
                                            }}>Ingredients</h3>
                                            <ul className="ingredients-list">
                                                {(recipe.ingredients || ['No ingredients found']).map((ingredient, i) => (
                                                    <li key={i}>{ingredient}</li>
                                                ))}
                                            </ul>
                                        </div>

                                        {/* Instructions Card */}
                                        <div className="recipe-card instructions-card">
                                            <h3 style={{
                                                fontSize: '1.4rem',
                                                fontWeight: '600',
                                                color: '#333',
                                                margin: '0 0 15px 0'
                                            }}>Instructions</h3>
                                            <ol className="instructions-list">
                                                {(recipe.steps || ['No instructions found']).map((step, i) => (
                                                    <li key={i}>{step}</li>
                                                ))}
                                            </ol>
                                        </div>

                                        {/* Stores Card */}
                                        <div className="recipe-card stores-card">
                                            <StoreLocator 
                                                recipeIngredients={recipe.ingredients || []}
                                                onStoresUpdate={(stores) => handleStoresUpdate(stores, index)}
                                            />
                                        </div>
                                        </div>
                                    </div>
                                ))}
                            </div>


                        </div>
                    ) : (
                        <>
                        {/* Tab-based button cards */}
                        <div className="recipe-action-cards" style={{ marginBottom: '30px' }}>
                        {activeTab === 'externally' ? (
                            /* External Recipe Sources */
                            <div style={{ 
                                display: 'flex', 
                                gap: '30px', 
                                alignItems: 'stretch', 
                                flexWrap: 'wrap',
                                justifyContent: 'center'
                            }}>
                                {/* Connect TikTok Card OR Back/Analyze navigation OR URL Form */}
                                {videos && videos.length > 0 ? (
                                    /* Tab-like navigation for video analysis */
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginBottom: '20px',
                                        width: '100%',
                                        justifyContent: 'space-between',
                                        padding: '0'
                                    }}>
                                        <div 
                                            className="action-button"
                                            onClick={() => {
                                                setVideos([]);
                                                setSelectedVideos([]);
                                                setAnalysisResults(null);
                                            }}
                                            style={{
                                                fontSize: '1.1rem',
                                                fontWeight: '500',
                                                color: '#666',
                                                cursor: 'pointer',
                                                paddingBottom: '5px',
                                                position: 'relative'
                                            }}
                                        >
                                            Back
                                        </div>
                                        <div 
                                            onClick={async () => {
                                                if (selectedVideos.length === 0 || loading) return;
                                                
                                                // Use existing handleAnalyzeWithTracking logic from AIAnalysisButton
                                                console.log('🔘 Analyze button clicked!');
                                                console.log('👤 User type - Trial:', subscription?.status === 'trial', 'Paid:', subscription?.status !== 'trial');
                                                console.log('📊 Selected videos count:', selectedVideos.length);
                                                
                                                setLoading(true);
                                                setError(null);
                                                
                                                try {
                                                    // For trial users, track usage per video
                                                    if (subscription?.status === 'trial') {
                                                        console.log('⏳ Processing trial user...');
                                                        const remaining = subscription?.plan?.trial_usage_limit - subscription?.trial_usage_count;
                                                        console.log('🔢 Remaining trial uses:', remaining);
                                                        
                                                        if (selectedVideos.length > remaining) {
                                                            const errorMsg = `Not enough trial uses remaining. You have ${remaining} uses left but selected ${selectedVideos.length} videos.`;
                                                            console.log('❌ Not enough uses:', errorMsg);
                                                            setError(errorMsg);
                                                            setLoading(false);
                                                            return;
                                                        }
                                                        
                                                        // Track usage for each video
                                                        console.log('📝 Tracking usage for each video...');
                                                        for (let i = 0; i < selectedVideos.length; i++) {
                                                            console.log(`📝 Tracking video ${i + 1}/${selectedVideos.length}`);
                                                            const result = await trackUsage('ai_usage', `Recipe Analysis - Video ${i + 1}/${selectedVideos.length}`);
                                                            console.log('📝 Track result:', result);
                                                            if (!result.success) {
                                                                const errorMsg = `Trial limit reached after analyzing ${i} videos`;
                                                                console.log('❌ Usage tracking failed:', errorMsg);
                                                                setError(errorMsg);
                                                                setLoading(false);
                                                                return;
                                                            }
                                                        }
                                                    } else {
                                                        console.log('💎 Processing premium user - no usage tracking needed');
                                                    }
                                                    
                                                    console.log('🚀 Starting analysis...');
                                                    // Then perform analysis
                                                    await analyzeSelectedVideos();
                                                } catch (error) {
                                                    console.error('❌ Analysis error:', error);
                                                    setError(`Failed to analyze videos: ${error.message}`);
                                                } finally {
                                                    setLoading(false);
                                                }
                                            }}
                                            className="action-button"
                                            style={{
                                                fontSize: '1.1rem',
                                                fontWeight: '500',
                                                color: selectedVideos.length === 0 || loading ? '#ccc' : '#666',
                                                cursor: selectedVideos.length === 0 || loading ? 'not-allowed' : 'pointer',
                                                paddingBottom: '5px',
                                                position: 'relative',
                                                opacity: loading ? 0.7 : 1
                                            }}
                                        >
                                            {loading ? 'Extracting...' : 'Extract'}
                                        </div>
                                    </div>
                                ) : showUrlForm ? (
                                    /* URL Form with Back and Extract buttons */
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginBottom: '20px',
                                        width: '100%',
                                        justifyContent: 'space-between',
                                        padding: '0'
                                    }}>
                                        {/* Back Button */}
                                        <div 
                                            className="action-button"
                                            onClick={() => {
                                                setShowUrlForm(false);
                                                setUrlInput('');
                                            }}
                                            style={{
                                                fontSize: '1.1rem',
                                                fontWeight: '500',
                                                color: '#666',
                                                cursor: 'pointer',
                                                paddingBottom: '5px',
                                                position: 'relative'
                                            }}
                                        >
                                            Back
                                        </div>

                                        {/* URL Input Form */}
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '15px',
                                            flex: '1',
                                            justifyContent: 'center',
                                            margin: '0 40px'
                                        }}>
                                            <input
                                                type="url"
                                                value={urlInput}
                                                onChange={(e) => setUrlInput(e.target.value)}
                                                placeholder="Paste recipe URL here..."
                                                style={{
                                                    padding: '12px 16px',
                                                    border: '2px solid #e0e0e0',
                                                    borderRadius: '8px',
                                                    fontSize: '16px',
                                                    width: '400px',
                                                    maxWidth: '100%',
                                                    outline: 'none',
                                                    transition: 'border-color 0.3s',
                                                }}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = '#0B303B';
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = '#e0e0e0';
                                                }}
                                            />
                                        </div>

                                        {/* Extract Button */}
                                        <div 
                                            onClick={async () => {
                                                if (!urlInput.trim() || loading) return;
                                                
                                                setLoading(true);
                                                setError(null);
                                                
                                                try {
                                                    const response = await fetch(`${apiBaseUrl}/api/claude/analyze-url/`, {
                                                        method: 'POST',
                                                        headers: {
                                                            'Content-Type': 'application/json',
                                                        },
                                                        body: JSON.stringify({ url: urlInput.trim() })
                                                    });
                                                    
                                                    if (!response.ok) {
                                                        throw new Error('Failed to extract recipe');
                                                    }
                                                    
                                                    const data = await response.json();
                                                    setAnalysisResults(data);
                                                    setShowAnalysis(true);
                                                    setShowUrlForm(false);
                                                    setUrlInput('');

                                                    // Clear any previous video selections since this is URL-based
                                                    setSelectedVideos([]);
                                                    setVideos([]);
                                                } catch (error) {
                                                    setError(`Failed to extract recipe: ${error.message}`);
                                                } finally {
                                                    setLoading(false);
                                                }
                                            }}
                                            className="action-button"
                                            style={{
                                                fontSize: '1.1rem',
                                                fontWeight: '500',
                                                color: !urlInput.trim() || loading ? '#ccc' : '#666',
                                                cursor: !urlInput.trim() || loading ? 'not-allowed' : 'pointer',
                                                paddingBottom: '5px',
                                                position: 'relative',
                                                opacity: loading ? 0.7 : 1
                                            }}
                                        >
                                            {loading ? 'Extracting...' : 'Extract'}
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {/* Connect TikTok Card - Only show when not in URL form mode */}
                                        {!showUrlForm && (
                                            <div
                                                onClick={loading ? undefined : handleTikTokLogin}
                                                className="recipe-action-card"
                                            style={{
                                                width: '200px',
                                                height: '280px',
                                                border: '2px solid #e0e0e0',
                                                borderRadius: '12px',
                                                backgroundColor: 'white',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: loading ? 'not-allowed' : 'pointer',
                                                transition: 'transform 0.3s, box-shadow 0.3s, border-color 0.3s',
                                                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                                                opacity: loading ? 0.7 : 1,
                                                padding: '30px 20px'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!loading) {
                                                    e.currentTarget.style.transform = 'translateY(-5px)';
                                                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.12)';
                                                    e.currentTarget.style.borderColor = '#0B303B';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!loading) {
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.08)';
                                                    e.currentTarget.style.borderColor = '#e0e0e0';
                                                }
                                            }}
                                        >
                                            <h3 style={{
                                                color: '#0B303B',
                                                fontSize: '1.2rem',
                                                fontWeight: '500',
                                                marginBottom: '30px',
                                                textAlign: 'center',
                                                lineHeight: 1.3
                                            }}>
                                                {loading ? 'Loading...' : 'Connect TikTok'}
                                            </h3>
                                            <div style={{
                                                fontSize: '60px',
                                                color: '#0B303B',
                                                fontWeight: '300',
                                                lineHeight: 1
                                            }}>
                                                +
                                            </div>
                                        </div>
                                        )}

                                        {/* Paste URL Card - Only show when not in URL form mode */}
                                        {!showUrlForm && (
                                            <div
                                                onClick={loading ? undefined : handlePasteUrl}
                                                className="recipe-action-card"
                                                style={{
                                                    width: '200px',
                                                    height: '280px',
                                                    border: '2px solid #e0e0e0',
                                                    borderRadius: '12px',
                                                    backgroundColor: 'white',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: loading ? 'not-allowed' : 'pointer',
                                                    transition: 'transform 0.3s, box-shadow 0.3s, border-color 0.3s',
                                                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                                                    opacity: loading ? 0.7 : 1,
                                                    padding: '30px 20px'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!loading) {
                                                        e.currentTarget.style.transform = 'translateY(-5px)';
                                                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.12)';
                                                        e.currentTarget.style.borderColor = '#0B303B';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!loading) {
                                                        e.currentTarget.style.transform = 'translateY(0)';
                                                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.08)';
                                                        e.currentTarget.style.borderColor = '#e0e0e0';
                                                    }
                                                }}
                                            >
                                                <h3 style={{
                                                    color: '#0B303B',
                                                    fontSize: '1.2rem',
                                                    fontWeight: '500',
                                                    marginBottom: '30px',
                                                    textAlign: 'center',
                                                    lineHeight: 1.3
                                                }}>
                                                    Paste URL
                                                </h3>
                                                <div style={{
                                                    fontSize: '60px',
                                                    color: '#0B303B',
                                                    fontWeight: '300',
                                                    lineHeight: 1
                                                }}>
                                                    +
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        ) : (
                            /* Internal Recipe Creation */
                            showChatInterface ? (
                                /* Chat Interface with Back button */
                                <>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginBottom: '20px',
                                        width: '100%',
                                        justifyContent: 'flex-start',
                                        padding: '0'
                                    }}>
                                        {/* Back Button */}
                                        <div 
                                            className="action-button"
                                                                                    onClick={() => {
                                            setShowChatInterface(false);
                                            // Optionally clear chat history when going back
                                            // setChatHistory([]);
                                        }}
                                            style={{
                                                fontSize: '1.1rem',
                                                fontWeight: '500',
                                                color: '#666',
                                                cursor: 'pointer',
                                                paddingBottom: '5px',
                                                position: 'relative'
                                            }}
                                        >
                                            Back
                                        </div>
                                    </div>
                                    
                                    {/* Chat Interface Content */}
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '30px',
                                        width: '100%',
                                        maxWidth: '1200px',
                                        margin: '0 auto'
                                    }}>
                                        <ChatInterface 
                                            analysisResults={null}
                                            isRecipeCreation={true}
                                            onRecipesConfirmed={handleRecipesConfirmed}
                                        />
                                    </div>
                                </>
                            ) : (
                                /* Create Recipe Card */
                                <div style={{ 
                                    display: 'flex', 
                                    gap: '30px', 
                                    alignItems: 'stretch', 
                                    flexWrap: 'wrap',
                                    justifyContent: 'center'
                                }}>
                                    <div
                                        onClick={loading ? undefined : handleCreateRecipe}
                                        className="recipe-action-card"
                                        style={{
                                            width: '200px',
                                            height: '280px',
                                            border: '2px solid #e0e0e0',
                                            borderRadius: '12px',
                                            backgroundColor: 'white',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: loading ? 'not-allowed' : 'pointer',
                                            transition: 'transform 0.3s, box-shadow 0.3s, border-color 0.3s',
                                            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                                            opacity: loading ? 0.7 : 1,
                                            padding: '30px 20px'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!loading) {
                                                e.currentTarget.style.transform = 'translateY(-5px)';
                                                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.12)';
                                                e.currentTarget.style.borderColor = '#0B303B';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!loading) {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.08)';
                                                e.currentTarget.style.borderColor = '#e0e0e0';
                                            }
                                        }}
                                    >
                                        <h3 style={{
                                            color: '#0B303B',
                                            fontSize: '1.2rem',
                                            fontWeight: '500',
                                            marginBottom: '30px',
                                            textAlign: 'center',
                                            lineHeight: 1.3
                                        }}>
                                            Create your recipe
                                        </h3>
                                        <div style={{
                                            fontSize: '60px',
                                            color: '#0B303B',
                                            fontWeight: '300',
                                            lineHeight: 1
                                        }}>
                                            +
                                        </div>
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                    
                    {activeTab === 'externally' && videos && videos.length > 0 && (
                        <div>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                                gap: '20px',
                            }}>
                                {videos.map(video => (
                                    <div 
                                        key={video.id}
                                        onClick={() => toggleVideoSelection(video)}
                                        style={{
                                            border: selectedVideos.some(v => v.id === video.id) 
                                                ? '2px solid #0B303B' 
                                                : '1px solid #ddd',
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            cursor: 'pointer',
                                            backgroundColor: 'white',
                                            position: 'relative'
                                        }}
                                    >
                                        <div style={{
                                            position: 'relative',
                                            width: '100%',
                                            height: '200px',
                                            backgroundColor: '#f0f0f0'
                                        }}>
                                            <img 
                                                src={`${apiBaseUrl}/api/tiktok/proxy-image/?url=${encodeURIComponent(video.cover_image_url)}`}
                                                alt={video.title || 'Video thumbnail'}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0
                                                }}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                        </div>
                                        <div style={{ padding: '15px' }}>
                                            <h3 style={{ 
                                                margin: '0 0 10px 0',
                                                fontSize: '1.1em',
                                                fontWeight: '500'
                                            }}>
                                                {video.title || 'Untitled'}
                                            </h3>
                                            <p style={{ 
                                                margin: '0',
                                                fontSize: '0.9em',
                                                color: '#666',
                                                lineHeight: '1.4'
                                            }}>
                                                {video.video_description || 'No description'}
                                            </p>
                                        </div>
                                        {selectedVideos.some(v => v.id === video.id) && (
                                            <div style={{
                                                position: 'absolute',
                                                top: '10px',
                                                right: '10px',
                                                backgroundColor: '#0B303B',
                                                color: 'white',
                                                borderRadius: '50%',
                                                width: '24px',
                                                height: '24px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                ✓
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                        </>
                    )}
                </div>
            

        </div>
    );
}

export default Planner;